<template>
  <div class="context-picker-modal">
    <b-modal
        id="modal-create-context"
        ref="modal-create-context"
        footer-class="flex-nowrap"
        no-close-on-backdrop
        centered
    >
      <template #modal-header>
        <h3>{{ $t('modals.createContext.title') }}</h3>
      </template>

      <template #default>
        <div class="m-4">
          <div v-if="availableCombinations.length === 0" class="text-center p-4">
            {{ $t('modals.createContext.noActions') }}
            <br>
            <b-button
                class="green-button-transparent border-0 mt-3"
                @click="$emit('cancel-modal')">
              {{ $t('buttons.close') }}
            </b-button>
          </div>
          <div v-else>
            <b-row class="mb-4" v-if="showActionPicker">
              <b-col cols="4">
                <label for="value-name">
                  {{ $t('modals.createContext.operation') }}
                </label>
              </b-col>
              <b-col cols="8">
                <b-form-group v-slot="{ ariaDescribedbyAction }">
                  <b-form-radio
                      v-for="actionObject in availableActions"
                      v-model="action"
                      class="mb-1"
                      :key="actionObject.value"
                      :aria-describedby="ariaDescribedbyAction"
                      name="action"
                      :disabled="actionObject.disabled"
                      :value="actionObject.value">{{ actionObject.text }}
                  </b-form-radio>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row class="mb-4">
              <b-col cols="4">
                <label for="value-name">
                  {{ $t('modals.createContext.value') }}
                </label>
              </b-col>
              <b-col cols="8">
                <div>
                  <b-form-group v-slot="{ ariaDescribedbyValue }">
                    <b-form-radio
                        v-for="option in availableSources"
                        :key="option.value"
                        v-model="source"
                        class="text-uppercase"
                        :class="{ 'mb-1': !option.isLast }"
                        :aria-describedby="ariaDescribedbyValue"
                        name="source"
                        :value="option.value"
                        :disabled="option.disabled">
                      {{ $t(`modals.createContext.source.${option.translationKey}`) }}
                    </b-form-radio>
                  </b-form-group>
                </div>
              </b-col>
            </b-row>
            <b-row class="mb-4" v-if="showEventPicker">
              <b-col cols="4">
                <label for="value-event">
                  {{ $t('settings.page.context.event.label') }}
                </label>
              </b-col>
              <b-col cols="8">
                <b-form-group v-slot="{ ariaDescribedbyEvent }">
                  <b-form-radio
                      v-for="option in eventOptions"
                      :key="option.value"
                      v-model="event"
                      class="mb-1"
                      :aria-describedby="ariaDescribedbyEvent"
                      name="event"
                      :value="option.value">{{ option.text }}
                  </b-form-radio>
                </b-form-group>
                <p class="event-hint mb-0">
                  {{ $t('settings.page.context.event.hint') }}
                </p>
              </b-col>
            </b-row>
          </div>
        </div>
      </template>
      <template #modal-footer>
        <b-button
            v-if="availableCombinations.length > 0"
            class="p-3 border-right-light-grey"
            block
            @click="$emit('cancel-modal')"
        >
          {{ $t('buttons.cancel') }}
        </b-button>
        <b-button
            v-if="availableCombinations.length > 0"
            variant="primary"
            class="p-3"
            block
            :disabled="disableCreateButton"
            @click="createEntry"
        >
          {{ $t('buttons.create') }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
export default {
  name: 'ModalCreateNewContext',
  props: {
    allActions: {
      type: Array,
    },
    sourceOptions: {
      type: Array,
    },
    availableCombinations: {
      type: Array,
    },
    // Only set for pages; see the prop comment in PageSettingsContext.vue.
    eventOptions: {
      type: Array,
      default: () => [],
    },
    // Set by ProjectSettingsSegments, which reuses this whole modal chain to
    // define a SEGMENT's own condition. A segment has no action — it is a
    // condition, not a rule — and ProjectSettingsSegments discards
    // entry.action on purpose (mergeContextCases). Leaving the radio on
    // screen meant "Ausblenden wenn Volumen über 5000" was stored as
    // "segment matches when volume is over 5000": the exact opposite of what
    // the redakteur just read. Better no choice at all than a visible choice
    // without effect, or a note explaining why a control does nothing.
    hideActionChoice: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      action: null,
      source: null,
      // 'onFetchMagazine' stays the default even if eventOptions ever
      // arrived in a different order — existing pages must not change.
      event: 'onFetchMagazine',
    };
  },
  created() {
    if (this.availableCombinations.length > 0) {
      this.action = this.availableCombinations[0].action;
      this.source = this.availableCombinations[0].source;
    }
  },
  mounted() {
    this.$refs['modal-create-context'].show();
  },
  computed: {
    disableCreateButton() {
      return !this.action || !this.source;
    },
    // See the hideActionChoice prop. The action itself is still set (from
    // the first available combination, in created()) so the source list and
    // the Create button keep working exactly as before — it is just not
    // asked for where it has no meaning.
    showActionPicker() {
      return !this.hideActionChoice;
    },
    // Only 'hidePage' rules can distinguish backend- from browser-evaluated
    // hiding; 'showPage' is still disabled elsewhere and elements
    // (showElement/hideElement) never pass eventOptions at all. Fix round
    // 2: 'segment' is excluded too — a segment-sourced rule can ONLY ever
    // fire under 'onReaderNavigate' (segments are resolved exclusively by
    // the reader, the backend has no segment awareness at all), so there is
    // no real choice to offer; PageSettings.vue's createContext() hard-codes
    // that event regardless of what this component sends. Showing the
    // picker anyway — defaulted to 'onFetchMagazine', the picker's own
    // default — invited a redakteur to leave it on the one value under
    // which the rule can never fire, with no error and no indication
    // anything was wrong.
    // Final review C1: 'form' (answers) is excluded for exactly the same
    // reason. An answer only exists in the visitor's browser
    // (files/_app_dev/js/answers.js), so the backend's onFetchMagazine
    // filter resolves 'form.<id>.<field>' to null and the rule never fires
    // there either. It, too, is forced to 'onReaderNavigate' in
    // PageSettings.vue's eventForSource().
    showEventPicker() {
      return this.action === 'hidePage'
        && this.source !== 'segment'
        && this.source !== 'form'
        && this.eventOptions.length > 0;
    },
    availableActions() {
      return this.allActions.map((action) => {
        const isDisabled = !this.availableCombinations.some((combination) => combination.action === action.value);
        return { ...action, disabled: isDisabled };
      });
    },
    availableSources() {
      return this.sourceOptions.map((source) => {
        const action = this.action || this.availableCombinations[0].action;
        const isDisabled = !this.availableCombinations.some((combination) => combination.source === source.value && combination.action === action);
        return { ...source, disabled: isDisabled };
      });
    },
  },
  methods: {
    createEntry() {
      this.$emit('create-context', {
        action: this.action,
        source: this.source,
        // Only relevant for 'hidePage'; the parent falls back to the old
        // default when this is undefined (showPage, elements).
        event: this.showEventPicker ? this.event : undefined,
      });
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep  .modal-dialog {
  .modal-content {
    border: none;
    label {
      color: #6c757d;
    }
  }

  .custom-control-input ~ .custom-control-label {
    color: #6c757d;
  }
  .custom-control-input[disabled] ~ .custom-control-label {
    cursor: not-allowed;
    color: #bbb;
  }
  button:disabled {
    cursor: not-allowed;
  }
}
</style>
